import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePageSeo } from '@/hooks/usePageSeo';
import { CATEGORIES, SITE_URL } from '@/config/site';
import { topics } from '@/mocks/topics';
import TopicCard from '@/components/feature/TopicCard';
import Reveal from '@/components/base/Reveal';
import Breadcrumb from '@/components/feature/Breadcrumb';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '话题广场',
  url: `${SITE_URL}/topics`,
  inLanguage: 'zh-CN',
  description: '按分类浏览当前正在发酵的热点话题，支持关键词搜索与热度、时间排序。',
};

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState<'heat' | 'new'>('heat');

  useEffect(() => {
    const c = searchParams.get('category');
    const k = searchParams.get('keyword');
    if (c) setCategory(c);
    if (k) setKeyword(k);
  }, [searchParams]);

  const categories = ['全部', ...CATEGORIES.map((c) => c.name)];

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    const list = topics.filter((t) => {
      const matchCat = category === '全部' || t.category === category;
      const matchKw =
        !kw ||
        t.title.includes(kw) ||
        t.summary.includes(kw) ||
        t.tags.some((tag) => tag.includes(kw));
      return matchCat && matchKw;
    });
    return list.sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : b.publishedAt.localeCompare(a.publishedAt),
    );
  }, [category, keyword, sort]);

  const handleCategory = (c: string) => {
    setCategory(c);
    setKeyword('');
    const next = new URLSearchParams(searchParams);
    if (c === '全部') next.delete('category');
    else next.set('category', c);
    next.delete('keyword');
    setSearchParams(next, { replace: true });
  };

  usePageSeo({
    title: '话题广场｜正在发酵的热点话题与讨论现场',
    description:
      '浏览吃瓜观察局话题广场，按娱乐、影视、消费、科技、体育、社会等分类查看正在发酵的热点话题，支持关键词搜索与热度排序，快速进入讨论现场。',
    keywords: '话题广场,热搜榜,网友讨论,吃瓜现场,热点话题,舆论',
    path: '/topics',
    jsonLd,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '话题广场' }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">话题广场</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里是所有正在发酵的话题集合。选一个分类，或者直接搜关键词，看看大家最近都在讨论什么、评论区吵成什么样。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-12">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-sm">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题标题或标签"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="flex items-center gap-1 self-start rounded-full border border-background-300 p-1">
                <button
                  type="button"
                  onClick={() => setSort('heat')}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === 'heat' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  按热度
                </button>
                <button
                  type="button"
                  onClick={() => setSort('new')}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === 'new' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  按最新
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-background-200 pt-4">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleCategory(c)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    category === c
                      ? 'bg-secondary-500 text-background-50'
                      : 'border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 个话题
          </p>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic, i) => (
                <Reveal key={topic.id} delay={i * 40} className="h-full">
                  <TopicCard topic={topic} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                <i className="ri-emotion-unhappy-line" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的话题，换个关键词试试。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  handleCategory('全部');
                }}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回首页看看热榜
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}